import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { collectiveReadings } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';
import { SEO, pageSEO } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CollectivePage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, {
        email: email,
        source: 'collective'
      });

      toast.success('Welcome to the Collective', {
        description: response.data.message
      });
      setEmail('');
    } catch (error) {
      console.error('Newsletter signup error:', error);
      toast.error('Subscription failed', {
        description: 'Please try again or check your email address.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasReadings = collectiveReadings.length > 0;
  const latestReading = hasReadings ? collectiveReadings[0] : null;

  return (
    <div className="bg-[var(--color-charcoal)]">
      <SEO {...pageSEO.collective} path="/collective" />
      
      {/* Hero Section - Full Width Image */}
      {hasReadings && latestReading ? (
        <section className="relative min-h-screen">
          {/* Full Background Image */}
          <div className="absolute inset-0">
            <img
              src={latestReading.image}
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-charcoal)] via-[var(--color-charcoal)]/60 to-[var(--color-charcoal)]/30" />
            <div className="absolute inset-0 bg-gradient-to-r from-[var(--color-charcoal)]/80 via-transparent to-[var(--color-charcoal)]/40" />
          </div>
          
          {/* Content Overlay */}
          <div className="relative min-h-screen flex flex-col justify-end pb-20 pt-32">
            <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2 }}
                className="max-w-3xl"
              >
                <span className="inline-block px-4 py-2 bg-[var(--color-gold)]/20 border border-[var(--color-gold)]/40 rounded-full text-[var(--color-gold)] text-sm tracking-[0.2em] uppercase mb-6">
                  Latest Reflection
                </span>
                
                <p className="text-[var(--color-gold)] text-lg md:text-xl tracking-[0.15em] uppercase mb-4">
                  {latestReading.month}
                </p>
                
                <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl mb-8 leading-[1.05] text-[var(--color-cream)]">
                  {latestReading.title}
                </h1>
                
                <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 leading-relaxed mb-10 max-w-2xl">
                  {latestReading.excerpt}
                </p>
                
                <Link
                  to={`/collective/${latestReading.slug}`}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-[var(--color-gold)] text-[var(--color-charcoal)] font-semibold rounded-full hover:bg-[var(--color-gold-light)] transition-all duration-300 group"
                >
                  Read This Month's Reflection
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-6 h-10 border-2 border-[var(--color-cream)]/30 rounded-full flex justify-center pt-2"
            >
              <div className="w-1 h-2 bg-[var(--color-gold)] rounded-full" />
            </motion.div>
          </div>
        </section>
      ) : (
        /* No Readings Yet - Coming Soon */
        <section className="relative min-h-[80vh] flex items-center pt-32 pb-20">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1757265500084-e3a97ddf007e?w=1920&q=80"
              alt=""
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-charcoal)] via-[var(--color-charcoal)]/70 to-[var(--color-charcoal)]" />
          </div>
          
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="max-w-3xl"
            >
              <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-6 block">
                Monthly Guidance
              </span>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mb-8 leading-[1.05]">
                The Collective
              </h1>
              <p className="text-xl text-[var(--color-cream)]/70 leading-relaxed mb-8">
                Monthly reflections for the collective journey. Coming soon.
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {/* About The Collective */}
      <section className="py-24 lg:py-32 bg-[var(--color-charcoal)]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-8 text-[var(--color-cream)]">
              What is The Collective?
            </h2>
            <p className="text-lg md:text-xl text-[var(--color-cream)]/70 leading-[1.9] mb-6">
              Each month, a reflection is offered to the collective — not as prediction, but as a mirror. These readings explore the emotional and energetic themes that may be alive for many of us during a particular season.
            </p>
            <p className="text-lg md:text-xl text-[var(--color-cream)]/70 leading-[1.9]">
              Take what resonates. Leave what does not. Use these reflections as an invitation to pause, inquire, and return to yourself.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-20 bg-[var(--color-deep-red)]/10 border-y border-[var(--color-deep-red)]/20">
        <div className="max-w-2xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="font-serif text-2xl md:text-3xl mb-4 text-[var(--color-cream)]">
              Receive Each Month's Reflection
            </h3>
            <p className="text-[var(--color-cream)]/60 mb-8">
              One reflection per month. Delivered with care. No noise.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                required
                className="flex-1 px-6 py-4 bg-[var(--color-charcoal)] border border-[var(--color-deep-red)]/30 rounded-full text-[var(--color-cream)] placeholder:text-[var(--color-cream)]/40 focus:outline-none focus:border-[var(--color-gold)]/50 transition-colors"
                data-testid="collective-newsletter-email"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-4 bg-[var(--color-gold)] text-[var(--color-charcoal)] font-medium rounded-full hover:bg-[var(--color-gold-light)] transition-all duration-300 disabled:opacity-50"
                data-testid="collective-newsletter-submit"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Archive Section */}
      {collectiveReadings.length > 1 && (
        <section className="py-24 lg:py-32 bg-[var(--color-charcoal-light)]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-4 block">
                Archive
              </span>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl">
                Past Reflections
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {collectiveReadings.slice(1).map((reading, index) => (
                <motion.div
                  key={reading.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/collective/${reading.slug}`}
                    className="group block"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[4/3] mb-6">
                      <img
                        src={reading.image}
                        alt={reading.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-charcoal)] via-transparent to-transparent opacity-70" />
                      <div className="absolute bottom-4 left-4">
                        <span className="text-[var(--color-gold)] text-sm tracking-wider">{reading.month}</span>
                      </div>
                    </div>
                    <h3 className="font-serif text-xl mb-2 group-hover:text-[var(--color-gold)] transition-colors">
                      {reading.title}
                    </h3>
                    <p className="text-[var(--color-cream)]/60 text-sm leading-relaxed">
                      {reading.excerpt}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
